import { build_description_editor_id, build_section_payload_table } from './view_job_profile_logic_helpers';

function get_table_editor_ids(section, sidx, subIdx, build_table_cell_editor_id) {
  return (section?.table?.rows || []).flatMap((row, rIdx) =>
    (row || []).map((_, cIdx) => build_table_cell_editor_id(sidx, rIdx, cIdx, subIdx)));
}

function get_description_editor_ids(section, sidx, subIdx) {
  return (section?.descriptions || []).map((_, dIdx) => build_description_editor_id(sidx, dIdx, subIdx));
}

function get_section_descriptions_payload(section) {
  return section.use_columns ? [] : (section.descriptions || []);
}

function get_section_subsections_payload(section, serialize_section) {
  return (section.subsections || []).map((subsection) => serialize_section(subsection));
}

function build_serialized_section(section, form_id, serialize_section) {
  return {
    ...build_serialized_section_base(section, form_id),
    descriptions: get_section_descriptions_payload(section),
    table: build_section_payload_table(section),
    show_on_advert: section.show_on_advert === true,
    subsections: get_section_subsections_payload(section, serialize_section),
  };
}
function build_serialized_section_base(section, form_id) {
  const form_id_value = get_serialized_section_form_id(section, form_id);
  const section_base = {
    id: get_serialized_section_id(section),
    form_id: form_id_value,
    title: get_serialized_section_title(section),
    use_columns: is_serialized_section_columned(section),
  };
  return section_base;
}
function get_serialized_section_id(section) {
  return section.id || null;
}
function get_serialized_section_form_id(section, form_id) {
  return section.form_id || form_id;
}
function get_serialized_section_title(section) {
  return section.title || '';
}
function is_serialized_section_columned(section) {
  return section.use_columns === true;
}
function build_classic_editor_config() {
  const config = {
    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' }
      ]
    },
    typing: { transformations: { remove: null } }
  };
  return config;
}

const methods = {
    async init_editors() {
      await this.$nextTick();
      this.sections.forEach((section, sidx) => {
        this.init_section_editors(sidx);
        (section.subsections || []).forEach((_, subIdx) => {
          this.init_section_editors(sidx, subIdx);
        });
      });
    },
    build_section_editor_id(prefix, sidx, item_idx, subIdx = null, nested_idx = null) {
      if (subIdx === null) {
        return nested_idx === null
          ? `${prefix}_${sidx}_${item_idx}`
          : `${prefix}_${sidx}_${item_idx}_${nested_idx}`;
      }
      return nested_idx === null
        ? `sub_${prefix}_${sidx}_${subIdx}_${item_idx}`
        : `sub_${prefix}_${sidx}_${subIdx}_${item_idx}_${nested_idx}`;
    },
    init_table_editors(section, sidx, subIdx = null) {
      section.table.rows.forEach((row, rIdx) => {
        row.forEach((_, cIdx) => {
          this.make_richedit(this.build_section_editor_id("table_cell", sidx, rIdx, subIdx, cIdx));
        });
      });
    },
    init_description_editors(section, sidx, subIdx = null) {
      (section.descriptions || []).forEach((_, dIdx) => {
        this.make_richedit(this.build_section_editor_id("description_text", sidx, dIdx, subIdx));
      });
    },
    init_section_editors(sidx, subIdx = null) {
      const section = this.get_content_block(sidx, subIdx);
      if (!section) return;
      if (section.use_columns && section.table?.rows) {
        this.init_table_editors(section, sidx, subIdx);
        return;
      }
      this.init_description_editors(section, sidx, subIdx);
    },

    async make_richedit(editor_id) {
      if (!this._editorInstances) this._editorInstances = {};
      if (this.has_editor_instance(editor_id)) return;
      const element = document.getElementById(editor_id);
      if (!element) return;
      await this.create_richedit_instance(editor_id, element);
    },
    has_editor_instance(editor_id) {
      return !!Reflect.get(this._editorInstances || {}, editor_id);
    },
    async create_richedit_instance(editor_id, element) {
      const ClassicEditor = window.ClassicEditor;
      if (!ClassicEditor) return;
      const editor = await ClassicEditor.create(element, build_classic_editor_config()).catch((e) => {
        console.error('Failed to create editor:', e);
        return null;
      });
      if (editor) Reflect.set(this._editorInstances, editor_id, editor);
    },

    async destroy_ck_editor(editor_id) {
      const editor = Reflect.get(this._editorInstances || {}, editor_id);
      if (!editor) return;
      
      try {
        await editor.destroy();
        Reflect.set(this._editorInstances, editor_id, null);
      } catch (e) {
        console.error('Failed to destroy editor:', e);
      }
    },

    async destroy_section_editors(sidx, subIdx = null) {
      const section = this.get_content_block(sidx, subIdx);
      if (!section) return;
      const editor_ids = section.use_columns && section.table
        ? get_table_editor_ids(section, sidx, subIdx, (row_idx, rIdx, cIdx, child_idx) =>
          this.build_table_cell_editor_id(row_idx, rIdx, cIdx, child_idx))
        : get_description_editor_ids(section, sidx, subIdx);
      editor_ids.forEach((editor_id) => {
        this.destroy_ck_editor(editor_id);
      });
    },

    async cleanup_editors() {
      const existingEditors = Object.keys(this._editorInstances || {});
      for (const id of existingEditors) {
        await this.destroy_ck_editor(id);
      }
    },

    serialize_section(section) {
      return build_serialized_section(section, this.form_detail.id, (subsection) => this.serialize_section(subsection));
    },

    build_sections_payload(sections = []) {
      return (sections || []).map(section => this.serialize_section(section));
    },

    // ---------------------------------------------------
    // FORM SUBMISSION
};

export default methods;

